import './styles/App.css';
import React, { useState, useEffect, useCallback, Suspense } from 'react';
import AccountLocked from './pages/AccountLocked';
import SetPassword from './pages/SetPassword';
import PastDueBanner from './components/Settings/PastDueBanner';
import { jwtDecode } from 'jwt-decode';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { TRANSFORM_USER } from './Transformations';
import { logout, ssoUserLogin, getUser } from './api/userDataService';
import { initAllAnimations } from './utils/scrollAnimations';
import { getTheme } from './api/themeService';
import { applyTheme, getStoredTheme } from './utils/theme';
import './styles/theme.css';

// Import the visual message components (keep these - they're lightweight)
import { VisualMessageProvider, useVisualMessage } from './components/Validation/VisualMessageContext';
import VisualMessageContainer from './components/Validation/VisualMessageContainer';
import { TooltipProvider } from './contexts/TooltipContext';
import { AIAdvisorProvider } from './contexts/AIAdvisorContext';
import PersistentAIPlayer from './components/AI/PersistentAIPlayer';
import ConsentGate, { userNeedsConsent } from './components/Functions/ConsentGate';

// Import the inactivity timeout hook (keep this - it's lightweight)
import useInactivityTimeout from './utils/useInactivityTimeout';
import { setActivityResetCallback } from './api/userDataService';

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false, // Don't refetch on window focus
      retry: 1, // Retry failed requests once
    },
  },
});

// LAZY LOADING: Convert page imports to lazy loaded components
const Login = React.lazy(() => import('./pages/Login'));
const Homepage = React.lazy(() => import('./pages/Homepage'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const Profile = React.lazy(() => import('./pages/Profile'));
const Education = React.lazy(() => import('./pages/Education'));
const CV = React.lazy(() => import('./pages/CV'));
const Settings = React.lazy(() => import('./pages/Settings'));
const Jobs = React.lazy(() => import('./pages/Jobs'));
const Career = React.lazy(() => import('./pages/Career'));

// Loading Component - matches your existing design pattern
const LoadingSpinner = () => (
  <div className="loader flex items-center justify-center min-h-screen bg-white">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 md:h-16 md:w-16 border-4 border-gray-200 border-t-theme-primary mx-auto mb-4"></div>
      <p className="text-gray-600 font-medium text-sm md:text-base">Loading...</p>
    </div>
  </div>
);

// Redirect legacy /profile (and any ?section=) to the renamed /about-me path routes
const ProfileRedirect = () => {
  const location = useLocation();
  const legacySection = new URLSearchParams(location.search).get('section');
  return <Navigate to={legacySection ? `/about-me/${legacySection}` : '/about-me'} replace />;
};

// AnimationWrapper component (unchanged)
const AnimationWrapper = ({ children }) => {
  const location = useLocation();
  
  useEffect(() => {
    const cleanup = initAllAnimations();
    window.scrollTo(0, 0);
    return () => typeof cleanup === 'function' && cleanup();
  }, [location.pathname]);
  
  return <>{children}</>;
};

function NotFound() {
  return (
    <div className="rd" style={{ minHeight: '80vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '2rem', background: 'var(--paper)' }}>
      <h1 style={{ fontSize: '6rem', fontWeight: 800, color: 'var(--accent)', lineHeight: 1 }}>404</h1>
      <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.5rem', color: 'var(--ink)' }}>Page not found</h2>
      <p style={{ color: 'var(--ink-2)', marginBottom: '2rem' }}>The page you're looking for doesn't exist or has been moved.</p>
      <div style={{ display: 'flex', gap: '0.75rem' }}>
        <a href="/homepage" style={{ padding: '0.5rem 1.25rem', background: 'var(--accent)', color: 'var(--on-accent)', borderRadius: 'var(--r-sm)', textDecoration: 'none', fontWeight: 600 }}>Go home</a>
      </div>
    </div>
  );
}

// Whether a user's paid access has lapsed. Applies only to accounts that must pay
// for their own access (individual self-signup, or alumni who left an org). Locked
// when the subscription is not active OR its expiry date has passed (belt-and-braces
// for the window before a Stripe webhook flips the status).
// A failed renewal isn't the end: Stripe retries over ~2 weeks and many recover
// on their own. Locking on the first failure would shut someone out of their CV
// over a card blip, so past_due keeps access (with a warning banner) until the
// grace period expires or Stripe gives up and fires subscription.deleted.
export const PAST_DUE_GRACE_DAYS = 14;

// Days left in the grace period, or null when not applicable.
export const pastDueDaysRemaining = (u) => {
  if (!u || u.subscriptionStatus !== 'past_due') return null;
  // No stamp (pre-existing past_due records) — treat as day one rather than
  // locking immediately.
  const since = u.pastDueSince ? new Date(u.pastDueSince).getTime() : Date.now();
  if (isNaN(since)) return PAST_DUE_GRACE_DAYS;
  const elapsedDays = (Date.now() - since) / (1000 * 60 * 60 * 24);
  return Math.max(0, Math.ceil(PAST_DUE_GRACE_DAYS - elapsedDays));
};

export const isSubscriptionLapsed = (u) => {
  if (!u) return false;
  const mustPay = u.accountStatus === 'alumni' || u.accountType === 'individual';
  if (!mustPay) return false;
  if (u.subscriptionStatus === 'past_due') {
    // Still inside the grace window: allow access, banner warns them.
    return pastDueDaysRemaining(u) <= 0;
  }
  if (u.subscriptionStatus !== 'active') return true;
  if (u.subscriptionExpiry && new Date(u.subscriptionExpiry).getTime() < Date.now()) return true;
  return false;
};

// Protected route guard - defined outside App to maintain stable component identity
// (defining inside App causes remount on every App re-render, losing child component state)
const Protected = ({ isAuthenticated, children }) => {
  if (!isAuthenticated) return <Navigate to="/login" />;
  // Check paywall
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    if (isSubscriptionLapsed(u)) return <Navigate to="/account-locked" />;
  } catch { /* proceed */ }
  return children;
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  // When true (authenticated user without recorded consent), the consent gate is rendered
  // INSTEAD of the protected app, so the routes never mount until consent is given.
  const [consentRequired, setConsentRequired] = useState(false);
  // When true (a paid account whose subscription has lapsed), AccountLocked is rendered
  // INSTEAD of the app so a mid-session lapse blocks access immediately, not just on nav.
  const [subscriptionLapsed, setSubscriptionLapsed] = useState(false);
  // Days left before a failed renewal locks the account; 0/null hides the banner.
  const [pastDueDays, setPastDueDays] = useState(0);
  const { showMessage } = useVisualMessage();

  // Inactivity timeout configuration (unchanged)
  const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes
  const WARNING_TIME = 5 * 60 * 1000; // Show warning 5 minutes before logout

  const handleLogout = useCallback((showLogoutMessage = false) => {
    logout();
    setIsAuthenticated(false);
    
    if (showLogoutMessage) {
      showMessage("You have been logged out due to inactivity", "info");
    }
  }, [showMessage]);

  const handleInactivityTimeout = useCallback(() => {
    handleLogout(true);
  }, [handleLogout]);

  // Use the inactivity timeout hook only when authenticated (unchanged)
  const { resetTimeout } = useInactivityTimeout(
    isAuthenticated ? handleInactivityTimeout : () => {}, 
    INACTIVITY_TIMEOUT
  );

  // Set up API activity tracking (unchanged)
  useEffect(() => {
    if (isAuthenticated) {
      setActivityResetCallback(resetTimeout);
    } else {
      setActivityResetCallback(null);
    }
  }, [isAuthenticated, resetTimeout]);

  // Recompute whether the consent gate must block the app whenever auth state changes.
  // (localStorage `user` is written immediately before isAuthenticated flips to true on login.)
  useEffect(() => {
    setConsentRequired(isAuthenticated === true ? userNeedsConsent() : false);
    // Block returning lapsed users immediately from the cached user (the server
    // refresh below then confirms/updates it).
    if (isAuthenticated === true) {
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        setSubscriptionLapsed(isSubscriptionLapsed(u));
        setPastDueDays(pastDueDaysRemaining(u) || 0);
      } catch { setSubscriptionLapsed(false); setPastDueDays(0); }
    } else {
      setSubscriptionLapsed(false);
      setPastDueDays(0);
    }
  }, [isAuthenticated]);

  // Keep subscription status fresh from the server so a lapse (subscription cancelled
  // or expired while still logged in) blocks access promptly, not only after re-login.
  useEffect(() => {
    if (isAuthenticated !== true) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await getUser();
        const fresh = response?.result?.user;
        if (!fresh || cancelled) return;
        const cached = JSON.parse(localStorage.getItem('user') || '{}');
        const merged = {
          ...cached,
          subscriptionStatus: fresh.subscriptionStatus,
          subscriptionExpiry: fresh.subscriptionExpiry,
          // Needed by the grace-period calc and the billing UI.
          pastDueSince: fresh.pastDueSince,
          cancelAtPeriodEnd: fresh.cancelAtPeriodEnd,
          hasBilling: fresh.hasBilling,
          subscriptionPlan: fresh.subscriptionPlan,
          accountStatus: fresh.accountStatus,
          accountType: fresh.accountType,
          accessState: fresh.accessState,
        };
        localStorage.setItem('user', JSON.stringify(merged));
        // The server decides this now, and enforces the same verdict on every
        // other endpoint. Only fall back to computing it locally against a
        // backend that predates accessState.
        if (merged.accessState) {
          setSubscriptionLapsed(merged.accessState === 'locked');
          setPastDueDays(merged.accessState === 'grace' ? (fresh.pastDueDaysRemaining || 0) : 0);
        } else {
          setSubscriptionLapsed(isSubscriptionLapsed(merged));
          setPastDueDays(pastDueDaysRemaining(merged) || 0);
        }
      } catch { /* keep cached state on error */ }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  // SSO Login - check for sso_token in URL on mount
  useEffect(() => {
    const checkSSOToken = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const ssoToken = urlParams.get('sso_token');
      
      if (ssoToken) {
        try {
          const result = await ssoUserLogin(ssoToken);
          
          if (result && result.token && result.user) {
            const transformedUser = TRANSFORM_USER(result.user);
            localStorage.setItem('token', result.token);
            localStorage.setItem('user', JSON.stringify(transformedUser));
            setIsAuthenticated(true);

            // Genuine login: arm the one-shot tooltip tour (consumed in TooltipContext).
            // Returning-user reloads intentionally do NOT set this.
            localStorage.setItem('pendingTooltipTour', 'true');
            // Dispatch custom event to notify TooltipContext of login
            window.dispatchEvent(new Event('userLogin'));
            
            resetTimeout();
            
            // Remove sso_token from URL
            urlParams.delete('sso_token');
            const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
            window.history.replaceState({}, '', newUrl);
            
            showMessage("SSO login successful!", "success");
          }
        } catch (error) {
          console.error("SSO login error:", error);
          showMessage("SSO login failed. Please try logging in manually.", "error");
          // Remove sso_token from URL even on error
          urlParams.delete('sso_token');
          const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
          window.history.replaceState({}, '', newUrl);
        }
      }
    };
    
    checkSSOToken();
  }, [resetTimeout, showMessage]);

  // Main authentication check - runs once on mount (unchanged)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        const currentTime = Date.now();
        const tokenExpiry = decoded.exp * 1000;
        
        if (tokenExpiry < currentTime) {
          logout();
          setIsAuthenticated(false);
        } else {
          setIsAuthenticated(true);
          // Dispatch login event if already authenticated (user returning)
          window.dispatchEvent(new Event('userLogin'));
        }
      } catch (error) {
        console.error("Invalid token:", error);
        logout();
        setIsAuthenticated(false);
      }
    } else {
      setIsAuthenticated(false);
    }
  }, []);

  // Load theme ONLY when user is authenticated (unchanged)
  useEffect(() => {
    if (isAuthenticated === true) {
      const loadTheme = async () => {
        try {
          await getTheme();
        } catch (error) {
        }
      };
      loadTheme();
    }
  }, [isAuthenticated]);

  // Token expiry warning - separate from main auth check (unchanged)
  useEffect(() => {
    if (isAuthenticated === true) {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const decoded = jwtDecode(token);
          const currentTime = Date.now();
          const tokenExpiry = decoded.exp * 1000;
          const timeUntilExpiry = tokenExpiry - currentTime;
          
          if (timeUntilExpiry < WARNING_TIME && timeUntilExpiry > 0) {
            showMessage("Your session will expire soon. Please save your work.", "warning");
          }
        } catch (error) {
          // Token parsing error handled in main useEffect
        }
      }
    }
  }, [isAuthenticated, showMessage, WARNING_TIME]);

  useEffect(() => {
    // Apply the stored theme to BOTH systems (data-theme for redesigned
    // token surfaces + .dark-mode for legacy page bodies). Single source
    // of truth in utils/theme.js.
    applyTheme(getStoredTheme());
  }, []);

  const handleLogin = async ({ token, refreshToken, user }) => {
    try {
      if (!token || !user) {
        throw new Error("Invalid login response structure");
      }
      const transformedUser = TRANSFORM_USER(user);
      localStorage.setItem('token', token); 
      localStorage.setItem('user', JSON.stringify(transformedUser));
      setIsAuthenticated(true);

      // Genuine login: arm the one-shot tooltip tour (consumed in TooltipContext).
      // Returning-user reloads intentionally do NOT set this.
      localStorage.setItem('pendingTooltipTour', 'true');
      // Dispatch custom event to notify TooltipContext of login
      window.dispatchEvent(new Event('userLogin'));
      
      // Reset the inactivity timer on successful login
      resetTimeout();
      
      showMessage("Login successful!", "success");
    } catch (error) {
      console.error("Login Error:", error.message);
      showMessage("Login failed. Please try again.", "error");
    }
  };

  // Enhanced logout that provides feedback (unchanged)
  const handleManualLogout = useCallback(() => {
    handleLogout();
    showMessage("You have been logged out successfully", "success");
  }, [handleLogout, showMessage]);

  // Subscription restored while the lock screen was up (they paid off-site).
  // The route form of the lock screen needs a real navigation; the inline form
  // clears as soon as the flag drops.
  const handleAccessRestored = useCallback((user) => {
    setSubscriptionLapsed(false);
    setPastDueDays(
      user && user.accessState === 'grace' ? (user.pastDueDaysRemaining || 0) : 0
    );
    if (window.location.pathname === '/account-locked') {
      window.location.assign('/homepage');
    }
  }, []);

  // Show loading while checking authentication (unchanged)
  if (isAuthenticated === null) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '18px'
      }}>
        Loading...
      </div>
    );
  }

  return (
    <AnimationWrapper>
      {/* First-login consent gate - rendered INSTEAD of the app until consent is given,
          so the protected routes never mount (removing the popup in devtools leaves a blank
          screen, not a usable app). Server-side enforcement would be needed to fully guarantee it. */}
      {isAuthenticated && consentRequired ? (
        <ConsentGate
          onAccept={() => setConsentRequired(false)}
          onDecline={handleManualLogout}
        />
      ) : isAuthenticated && subscriptionLapsed ? (
        // Paid access lapsed — render the lock screen instead of the app so no
        // route mounts. Cannot be dismissed without resubscribing or logging out.
        <AccountLocked onLogout={handleManualLogout} onAccessRestored={handleAccessRestored} />
      ) : (
      /* SUSPENSE WRAPPER: This handles lazy loading fallbacks */
      <Suspense fallback={<LoadingSpinner />}>
        {isAuthenticated && pastDueDays > 0 && (
          <PastDueBanner daysRemaining={pastDueDays} />
        )}
        <Routes>
          <Route
            path="/login"
            element={!isAuthenticated ? <Login onLogin={handleLogin} /> : <Navigate to="/homepage" />}
          />
          {/* Public: reached from the welcome email before an account has a
              password, so it must work while signed out. */}
          <Route path="/set-password" element={<SetPassword />} />
          <Route
            path="/account-locked"
            element={isAuthenticated ? <AccountLocked onLogout={handleManualLogout} onAccessRestored={handleAccessRestored} /> : <Navigate to="/login" />}
          />
          <Route
            path="/homepage"
            element={<Protected isAuthenticated={isAuthenticated}><Homepage onLogout={handleManualLogout} /></Protected>}
          />
          <Route
            path="/dashboard"
            element={<Protected isAuthenticated={isAuthenticated}><Dashboard onLogout={handleManualLogout} /></Protected>}
          />
          <Route
            path="/about-me"
            element={<Protected isAuthenticated={isAuthenticated}><Profile onLogout={handleManualLogout} /></Protected>}
          />
          {/* Section detail pages: /about-me/personal-information, /about-me/bio-video, … */}
          <Route
            path="/about-me/:section"
            element={<Protected isAuthenticated={isAuthenticated}><Profile onLogout={handleManualLogout} /></Protected>}
          />
          {/* Legacy route — redirect /profile (and any ?section=) to /about-me */}
          <Route path="/profile" element={<ProfileRedirect />} />
          <Route
            path="/education"
            element={<Protected isAuthenticated={isAuthenticated}><Education onLogout={handleManualLogout} /></Protected>}
          />
          {/* Section detail pages: /education/currently-studying, /education/existing-qualifications, … */}
          <Route
            path="/education/:section"
            element={<Protected isAuthenticated={isAuthenticated}><Education onLogout={handleManualLogout} /></Protected>}
          />
          <Route
            path="/cv"
            element={<Protected isAuthenticated={isAuthenticated}><CV onLogout={handleManualLogout} /></Protected>}
          />
          <Route
            path="/settings"
            element={<Protected isAuthenticated={isAuthenticated}><Settings onLogout={handleManualLogout} /></Protected>}
          />
          <Route
            path="/jobs"
            element={<Protected isAuthenticated={isAuthenticated}><Jobs onLogout={handleManualLogout} /></Protected>}
          />
          <Route
            path="/career"
            element={<Protected isAuthenticated={isAuthenticated}><Career onLogout={handleManualLogout} /></Protected>}
          />
          {/* Section detail pages: /career/goals, /career/jobs */}
          <Route
            path="/career/:section"
            element={<Protected isAuthenticated={isAuthenticated}><Career onLogout={handleManualLogout} /></Protected>}
          />
          <Route
            path="/"
            element={<Navigate to={isAuthenticated ? "/homepage" : "/login"} />}
          />
          <Route
            path="*"
            element={isAuthenticated ? <NotFound /> : <Navigate to="/login" />}
          />
        </Routes>
      </Suspense>
      )}

      <VisualMessageContainer />

      {/* Persistent AI Career Advisor - stays visible across pages (hidden while consent is pending) */}
      {isAuthenticated && !consentRequired && <PersistentAIPlayer />}
    </AnimationWrapper>
  );
}

function AppWrapper() {
  return (
    <QueryClientProvider client={queryClient}>
      <VisualMessageProvider>
        <TooltipProvider>
          <AIAdvisorProvider>
            <Router>
              <App />
            </Router>
          </AIAdvisorProvider>
        </TooltipProvider>
      </VisualMessageProvider>
    </QueryClientProvider>
  );
}

export default AppWrapper;